import React, { useState } from 'react';
import MainMenu from './MainMenu';
import App from './App';
import MultiplayerGame from './MultiplayerGame';
import { useAuth0 } from '@auth0/auth0-react';

// Detect if user is on a mobile device
const detectMobileDevice = () => {
  const userAgent = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
  const touchSupport = 'ontouchstart' in window;
  const smallScreen = window.innerWidth <= 768;

  return userAgent || (touchSupport && smallScreen);
};

function AppWrapper() {
  const { user, isAuthenticated, isLoading } = useAuth0();
  const [mode, setMode] = useState(null); // null, 'single', 'multiplayer', or 'ranked'
  const [wsUrl, setWsUrl] = useState('');
  const [shouldAutoReconnect, setShouldAutoReconnect] = useState(false); // Flag for auto-reconnection
  const [viewMode, setViewMode] = useState(detectMobileDevice() ? 'mobile' : 'desktop'); // desktop or mobile
  const [preferredColor, setPreferredColor] = useState('blue'); // blue, red, orange, purple, yellow
  const [userUsername, setUserUsername] = useState(() => {
    // Load username from localStorage if user has set one
    const saved = localStorage.getItem('blockRivalsUsername');
    return saved || null;
  });

  // Update username when authentication status changes
  React.useEffect(() => {
    const saved = localStorage.getItem('blockRivalsUsername');
    setUserUsername(saved || null);
  }, [isAuthenticated]);

  // Generate player name based on auth status
  const getPlayerName = () => {
    // If username is set in localStorage, use it
    if (userUsername) {
      return userUsername;
    }
    // If authenticated but no custom username, use "Player" + numbers
    if (isAuthenticated) {
      // Check for session-specific Player name
      let sessionPlayerName = sessionStorage.getItem('blockRivalsSessionPlayerName');
      if (!sessionPlayerName) {
        sessionPlayerName = `Player${Math.floor(Math.random() * 10000)}`;
        sessionStorage.setItem('blockRivalsSessionPlayerName', sessionPlayerName);
      }
      return sessionPlayerName;
    }
    // Default to "Guest" + random numbers for non-authenticated users
    // Use sessionStorage to keep the same guest name during the session
    let sessionGuestName = sessionStorage.getItem('blockRivalsSessionGuestName');
    if (!sessionGuestName) {
      sessionGuestName = `Guest${Math.floor(Math.random() * 10000)}`;
      sessionStorage.setItem('blockRivalsSessionGuestName', sessionGuestName);
    }
    return sessionGuestName;
  };

  const handleSelectMode = (selectedMode, url, autoReconnect = false) => {
    setMode(selectedMode);
    setShouldAutoReconnect(autoReconnect);
    if (url) {
      setWsUrl(url);
    }
  };

  const handleReturnToMenu = () => {
    setMode(null);
    setWsUrl('');
    setShouldAutoReconnect(false);
  };

  const handleUpdateSettings = (newViewMode, newColor) => {
    setViewMode(newViewMode);
    setPreferredColor(newColor);
  };

  const playerName = getPlayerName();

  if (mode === 'single') {
    return <App onReturnToMenu={handleReturnToMenu} initialViewMode={viewMode} initialColor={preferredColor} playerName={playerName} />;
  }

  if (mode === 'multiplayer') {
    return <MultiplayerGame onReturnToMenu={handleReturnToMenu} wsUrl={wsUrl} preferredColor={preferredColor} viewMode={viewMode} playerName={playerName} user={user} isRanked={false} autoReconnect={shouldAutoReconnect} />;
  }

  if (mode === 'ranked') {
    return <MultiplayerGame onReturnToMenu={handleReturnToMenu} wsUrl={wsUrl} preferredColor={preferredColor} viewMode={viewMode} playerName={playerName} user={user} isRanked={true} autoReconnect={shouldAutoReconnect} />;
  }

  return <MainMenu onSelectMode={handleSelectMode} onUpdateSettings={handleUpdateSettings} viewMode={viewMode} preferredColor={preferredColor} />;
}

export default AppWrapper;
